import { useRef } from 'react';
import { enableTraceToDatasetPromotion } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useQuickSubmit } from '@gonfalon/dom/useQuickSubmit';
import { RESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { useTypedAppForm } from '@gonfalon/forms-experimental/hooks';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import type { AnnotationResponse } from '@gonfalon/openapi/types';
import { usePutAnnotation } from '@gonfalon/rest-api/queries/annotations';
import {
  Button,
  Heading,
  Label,
  Text,
  TextArea,
  TextField,
  toastQueue,
  ToggleButton,
  ToggleButtonGroup,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useStore } from '@tanstack/react-form';

import { DatasetPicker } from './DatasetPicker';
import type { AnnotationBody, Correctness, DatasetOption } from './types';
import { useTracePromotion } from './useTracePromotion';

import styles from './ReviewForm.module.css';

const DEFAULT_VALUES: AnnotationBody = {
  correctness: undefined,
  explanation: '',
  expectedOutputOverride: '',
};

export interface ReviewFormProps {
  annotation: AnnotationResponse | undefined;
  traceId: string;
  spanId: string;
  spanTimestamp: string;
  projectKey: string;
  envKey: string;
  actualOutput?: string;
}

export const ReviewForm = ({
  annotation,
  traceId,
  spanId,
  spanTimestamp,
  projectKey,
  envKey,
  actualOutput,
}: ReviewFormProps) => {
  const { mutateAsync: putAnnotation } = usePutAnnotation();

  const pendingDataset = useRef<DatasetOption | undefined>(undefined);
  const { promote, isPromoting } = useTracePromotion({
    onCompleted: () => {
      const dataset = pendingDataset.current;
      pendingDataset.current = undefined;
      toastQueue.add({
        title: `Added to ${dataset?.name ?? 'the dataset'}`,
        description: 'The span is now available as a dataset row.',
        status: 'success',
      });
    },
    onFailed: (error) => {
      const dataset = pendingDataset.current;
      pendingDataset.current = undefined;
      toastQueue.add({
        title: `Could not add to ${dataset?.name ?? 'the dataset'}`,
        description: `${error.code}: ${error.message}`,
        status: 'error',
      });
    },
  });

  const form = useTypedAppForm<AnnotationBody>({
    defaultValues: annotation
      ? {
          correctness: annotation.correctness ?? undefined,
          explanation: annotation.explanation ?? '',
          expectedOutputOverride: annotation.expectedOutputOverride ?? '',
        }
      : DEFAULT_VALUES,
    onSubmit: async ({ value, formApi }) => {
      try {
        await putAnnotation({ projKey: projectKey, envKey, traceId, spanId, body: { ...value, spanTimestamp } });
        toastQueue.add({ title: 'Review saved', status: 'success' });
        // The Save button disables on a pristine form, and `isDirty` is a sticky per-field flag
        // rather than a comparison against the saved values, so clear it. `form.reset` would also
        // do it, but it rewrites the values and leaves the inputs blank.
        for (const field of Object.keys(formApi.state.fieldMeta) as Array<keyof AnnotationBody>) {
          formApi.setFieldMeta(field, (prev) => ({ ...prev, isDirty: false }));
        }
      } catch (error) {
        const restError = error instanceof RESTAPIError ? error : undefined;
        toastQueue.add({
          title: 'Could not save review',
          description: restError?.message ?? 'Something went wrong. Try again.',
          status: 'error',
        });
      }
    },
  });

  // Inputs are locked while the save is in flight, so an edit made mid-request can't end up
  // silently counted as saved when the response clears the dirty flags above.
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  // Cmd+Enter submits from either textarea. The hook's default path calls requestSubmit on the
  // nearest form element, and there isn't one here, so it gets a callback instead. The guard
  // reads form state at keypress time and mirrors SubmitButton's, so the keyboard can never
  // submit something the button refuses.
  const { props: quickSubmitProps } = useQuickSubmit({
    onSubmit: () => {
      const { isValid, isDirty, canSubmit } = form.state;
      if (form.state.isSubmitting || !isValid || !isDirty || !canSubmit) {
        return;
      }
      void form.handleSubmit();
    },
  });

  const handleSelectDataset = (dataset: DatasetOption) => {
    if (isPromoting) {
      return;
    }
    pendingDataset.current = dataset;
    promote({
      traceId,
      spanId,
      projectKey,
      datasetId: dataset.id,
      envKey,
      timestamp: spanTimestamp,
      annotationId: annotation?.id,
    });
  };

  return (
    <form.AppForm>
      <div className={styles.form}>
        <Heading level={3}>
          <Message id="observability.spanReview.heading" />
        </Heading>

        <form.AppField name="correctness">
          {(field) => (
            <div className={styles.field}>
              <Label>
                <Message id="observability.spanReview.correctnessLabel" />
              </Label>
              <ToggleButtonGroup
                selectionMode="single"
                isDisabled={isSubmitting}
                selectedKeys={field.state.value !== undefined ? [field.state.value] : []}
                onSelectionChange={(keys) => {
                  const key = [...keys][0];
                  const next: Correctness | undefined = key === 'correct' || key === 'incorrect' ? key : undefined;
                  field.handleChange(next);
                  if (next !== 'incorrect') {
                    form.setFieldValue('expectedOutputOverride', '');
                  }
                }}
              >
                <ToggleButton id="correct" aria-label={msg('observability.spanReview.correct')}>
                  <Icon name="thumb-up-outline" size="small" />
                  {msg('observability.spanReview.correct')}
                </ToggleButton>
                <ToggleButton id="incorrect" aria-label={msg('observability.spanReview.incorrect')}>
                  <Icon name="thumb-down-outline" size="small" />
                  {msg('observability.spanReview.incorrect')}
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.correctness}>
          {(correctness) =>
            correctness === 'incorrect' && (
              <form.AppField name="expectedOutputOverride">
                {(field) => (
                  <div className={styles.field}>
                    <div className={styles.fieldHeader}>
                      <Label>
                        <Message id="observability.spanReview.expectedOutputLabel" />
                      </Label>
                      <Button
                        variant="minimal"
                        size="small"
                        isDisabled={!actualOutput || isSubmitting}
                        onPress={() => form.setFieldValue('expectedOutputOverride', actualOutput ?? '')}
                      >
                        {msg('observability.spanReview.insertActualOutput')}
                      </Button>
                    </div>
                    <TextField
                      aria-label={msg('observability.spanReview.expectedOutputLabel')}
                      isDisabled={isSubmitting}
                      value={field.state.value ?? ''}
                      onChange={(value) => field.handleChange(value)}
                    >
                      <TextArea {...quickSubmitProps} />
                      <Text slot="description">
                        <Message id="observability.spanReview.expectedOutputHint" />
                      </Text>
                    </TextField>
                  </div>
                )}
              </form.AppField>
            )
          }
        </form.Subscribe>

        <form.AppField name="explanation">
          {(field) => (
            <TextField
              isDisabled={isSubmitting}
              value={field.state.value ?? ''}
              onChange={(value) => field.handleChange(value)}
            >
              <Label>
                <Message id="observability.spanReview.explanationLabel" />
              </Label>
              <TextArea placeholder={msg('observability.spanReview.explanationPlaceholder')} {...quickSubmitProps} />
            </TextField>
          )}
        </form.AppField>

        <div className={styles.actions}>
          <form.SubmitButton>{msg('observability.spanReview.save')}</form.SubmitButton>
          {enableTraceToDatasetPromotion() && (
            <DatasetPicker
              projectKey={projectKey}
              onSelect={handleSelectDataset}
              isDisabled={isPromoting}
              isPromoting={isPromoting}
            />
          )}
        </div>
      </div>
    </form.AppForm>
  );
};
